/**
 * Google Analytics 4 wrapper.
 *
 * Loads gtag.js lazily and only after the visitor has explicitly accepted
 * analytics cookies (see CookieConsent component). This keeps the site
 * GDPR-compliant under EU/Belgian rules.
 *
 * Required env var: REACT_APP_GA_MEASUREMENT_ID (e.g. G-XXXXXXXXXX) — leave it
 * empty in development to disable tracking entirely.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const CONSENT_KEY = 'sk_analytics_consent';

let initialized = false;

const isBrowser = () => typeof window !== 'undefined';

const isPrerender = () =>
  isBrowser() &&
  navigator &&
  /ReactSnap|HeadlessChrome|prerender/i.test(navigator.userAgent);

export const hasAnalyticsConsent = () => {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(CONSENT_KEY) === 'granted';
  } catch (err) {
    return false;
  }
};

export const setAnalyticsConsent = (granted) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch (err) {
    /* storage blocked, ignore */
  }
  if (granted) {
    initAnalytics();
  }
};

export const initAnalytics = () => {
  if (initialized) return;
  if (!isBrowser() || isPrerender()) return;
  if (!MEASUREMENT_ID) return;
  if (!hasAnalyticsConsent()) return;

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  initialized = true;
};

export const trackPageview = (path) => {
  if (!isBrowser() || isPrerender()) return;
  if (!MEASUREMENT_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (!initialized) initAnalytics();
  if (!window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const trackEvent = (name, params = {}) => {
  if (!isBrowser() || isPrerender()) return;
  if (!MEASUREMENT_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (!window.gtag) return;
  window.gtag('event', name, params);
};
